import React, { useCallback, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { getIsWorkflowPhased } from '@aline/common';
import { Typography } from '@mui/material';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { getNonSigningStartedColumns } from 'src/components/workflow/phase-column-helpers';
import { DestinationPicker } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';
import { useBoardColumns } from 'src/hooks/boards';
import { useStartWorkflowWork } from 'src/hooks/workflow/workflow';
import { pushNotification } from 'src/notifications';
import { getIsSigningPhaseType } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import type { ParkedWorkflowMeta } from '../../use-parked-workflow-completion';

export type StartWorkflowMeta = Pick<ParkedWorkflowMeta, 'column' | 'id' | 'startedAt'> & {
  boardId: UUID;
  phases: readonly { __typename?: string }[];
};

const useStyles = makeStyles()(theme => ({
  columnField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(2),
  },
  columnLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
  },
}));

/**
 * Starts tasks. A task starts right away even when its workflow has not; starting the workflow
 * is its own choice, so this then offers to start it, or to move it out of Triage.
 */
export function useStartTask(workflow: StartWorkflowMeta) {
  const [startTaskMutation, { loading }] = Apollo.useStartTaskMutation();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const needsPrompt = getNeedsWorkflowPrompt(workflow);

  /** For a task that started some other way, such as being created started. */
  const offerWorkflowStart = useCallback(() => {
    if (needsPrompt) setIsPromptOpen(true);
  }, [needsPrompt]);

  const requestStart = useCallback(
    async (taskId: UUID) => {
      const { error } = await startTaskMutation({ variables: { input: { taskId } } });
      if (error) {
        pushNotification({ key: uuid4(), message: error.message, variant: 'error' });
        return;
      }
      offerWorkflowStart();
    },
    [offerWorkflowStart, startTaskMutation]
  );

  const dismissPrompt = useCallback(() => setIsPromptOpen(false), []);

  return { dismissPrompt, isPromptOpen, loading, offerWorkflowStart, requestStart };
}

interface StartWorkflowAfterTaskDialogProps {
  onClose: () => void;
  open: boolean;
  workflow: StartWorkflowMeta;
}

/**
 * Asked once a task has started on a workflow that has not, or on a phaseless one still in Triage.
 * A phaseless workflow's column is the user's to pick; a phased one follows its first phase, so
 * starting it is a plain yes or no.
 */
export function StartWorkflowAfterTaskDialog({
  onClose,
  open,
  workflow,
}: StartWorkflowAfterTaskDialogProps): JSX.Element | null {
  if (!open) return null;
  if (getIsWorkflowPhased(workflow.phases, getIsSigningPhaseType))
    return <StartPhasedWorkflowDialog workflow={workflow} onClose={onClose} />;
  return <StartPhaselessWorkflowDialog workflow={workflow} onClose={onClose} />;
}

interface OpenDialogProps {
  onClose: () => void;
  workflow: StartWorkflowMeta;
}

function StartPhasedWorkflowDialog({ onClose, workflow }: OpenDialogProps): JSX.Element {
  const { startWorkflowWork } = useStartWorkflowWork(workflow.id);
  const confirm = useCallback(() => startWorkflowWork(), [startWorkflowWork]);

  return (
    <ConfirmationDialog
      hideIcon
      open
      message={`The task has started. This workflow is still in "${workflow.column.name}". Starting it also starts its first phase, notifies that phase's assignees and moves the workflow to the phase's column.`}
      primaryText="Start Workflow"
      secondaryText="Not Now"
      title="Start the workflow too?"
      onClickPrimary={confirm}
      onClose={onClose}
    />
  );
}

function StartPhaselessWorkflowDialog({ onClose, workflow }: OpenDialogProps): JSX.Element {
  const { classes } = useStyles();
  const { startWorkflowWork } = useStartWorkflowWork(workflow.id);
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const { boardColumns, loading, signingColumnId } = useBoardColumns(workflow.boardId);
  const startedColumns = useMemo(
    () => getNonSigningStartedColumns(boardColumns, signingColumnId),
    [boardColumns, signingColumnId]
  );
  const [pickedColumnId, setPickedColumnId] = useState<UUID | null>(null);
  const columnId = pickedColumnId ?? startedColumns.at(0)?.id ?? null;
  const isStarted = Boolean(workflow.startedAt);
  const confirm = useCallback(async () => {
    if (!columnId) return;
    if (!isStarted) await startWorkflowWork(columnId);
    else
      await updateWorkflow({
        variables: { input: { props: { columnId }, workflowId: workflow.id } },
      });
  }, [columnId, isStarted, startWorkflowWork, updateWorkflow, workflow.id]);
  const columnName = workflow.column.name;

  return (
    <ConfirmationDialog
      hideIcon
      open
      message={`The task has started. This workflow is still in "${columnName}". ${isStarted ? 'Move it' : 'Start the workflow and move it'} to a Started column?`}
      primaryDisabledReason={getPickDisabledReason({ columnId, loading })}
      primaryText={isStarted ? 'Move' : 'Start and Move'}
      secondaryText={`Keep in ${columnName}`}
      title={`Move out of ${columnName}?`}
      onClickPrimary={confirm}
      onClose={onClose}
    >
      <div className={classes.columnField}>
        <Typography className={classes.columnLabel}>Move to</Typography>
        <DestinationPicker
          boardId={workflow.boardId}
          columns={startedColumns}
          placeholder="Pick a column"
          selectedColumnId={columnId}
          onChange={setPickedColumnId}
        />
      </div>
    </ConfirmationDialog>
  );
}

function getNeedsWorkflowPrompt(workflow: StartWorkflowMeta): boolean {
  if (!workflow.startedAt) return true;
  return (
    workflow.column.category === Apollo.ColumnCategory.Triage &&
    !getIsWorkflowPhased(workflow.phases, getIsSigningPhaseType)
  );
}

function getPickDisabledReason(options: { columnId: UUID | null; loading: boolean }): string {
  if (options.columnId) return '';
  if (options.loading) return 'Loading columns...';
  return 'This board has no Started column to move to.';
}
